import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useChatStore } from '../stores/chatStore';
import { useClipboardStore } from '../stores/clipboardStore';
import { useSettingsStore } from '../stores/settingsStore';
import { usePWAStore } from '../stores/pwaStore';
import { useTouchDevice } from '../hooks/useTouchDevice';
import ConversationItem from './ConversationItem';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import logo from '../assets/logo.svg';
import DOMPurify from 'dompurify';
import SubscriptionStatus from './subscription/SubscriptionStatus';
import { convertMarkdownToHtml } from '../utils/markdownToHtml';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { config } from '../config';

interface SidebarProps {
  isMobileOpen: boolean;
  onClose: () => void;
  currentView: 'chat' | 'clipboard' | 'trash' | 'documents' | 'file-upload' | 'folders' | 'subscription' | 'chat-manage';
  onViewChange: (view: 'chat' | 'clipboard' | 'trash' | 'documents' | 'file-upload' | 'folders' | 'subscription' | 'chat-manage') => void;
}

interface DeletedItem {
  id: string;
  title?: string;
  deleted_at: string;
  content_variants?: Record<string, string>;
  messages?: any[];
}

export default function Sidebar({ isMobileOpen, onClose, currentView, onViewChange }: SidebarProps) {
  const { user, stats, logout, loadStats } = useAuthStore();
  const {
    conversations,
    currentConversation,
    loadConversations,
    loadConversation,
    deleteConversation,
    createConversation
  } = useChatStore();
  const { items: clipboardItems, total: clipboardTotal, deleteItem, loadItems } = useClipboardStore();
  const { openSettings } = useSettingsStore();
  const { isInstallable, isStandalone, promptInstall, isInstalling } = usePWAStore();
  const isTouch = useTouchDevice();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isClipboardExpanded, setIsClipboardExpanded] = useState(false);
  const [isConversationsExpanded, setIsConversationsExpanded] = useState(false);
  const [isDocumentsExpanded, setIsDocumentsExpanded] = useState(false);
  const [isTrashExpanded, setIsTrashExpanded] = useState(false);
  const [deletedConversations, setDeletedConversations] = useState<DeletedItem[]>([]);
  const [deletedClipboardItems, setDeletedClipboardItems] = useState<DeletedItem[]>([]);
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  const previousView = useRef(currentView);

  // Load conversations and stats on mount
  useEffect(() => {
    loadConversations();
    loadStats();
  }, []);

  // Sync expansion state with current view
  useEffect(() => {
    if (currentView === 'chat') {
      setIsConversationsExpanded(true);
      setIsClipboardExpanded(false);
      setIsDocumentsExpanded(false);
      setIsTrashExpanded(false);
    } else if (currentView === 'clipboard') {
      setIsClipboardExpanded(true);
      setIsConversationsExpanded(false);
      setIsDocumentsExpanded(false);
      setIsTrashExpanded(false);
    } else if (currentView === 'documents' || currentView === 'file-upload' || currentView === 'folders') {
      setIsDocumentsExpanded(true);
      setIsConversationsExpanded(false);
      setIsClipboardExpanded(false);
      setIsTrashExpanded(false);
    } else if (currentView === 'trash') {
      setIsTrashExpanded(true);
      setIsConversationsExpanded(false);
      setIsClipboardExpanded(false);
      setIsDocumentsExpanded(false);
      loadDeletedItems();
    }
  }, [currentView]);

  // Reload trash count when leaving trash view to catch any changes made in TrashView
  useEffect(() => {
    if (previousView.current === 'trash' && currentView !== 'trash') {
      // User just left trash view, reload to get updated counts
      loadDeletedItems();
    }
    previousView.current = currentView;
  }, [currentView]);

  // Modal focus trap and ESC handler
  useEffect(() => {
    if (!previewItem) return;

    // Handle ESC key to close modal
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreviewItem(null);
      }
    };

    // Focus the close button when modal opens
    setTimeout(() => {
      const closeButton = document.querySelector<HTMLButtonElement>('[data-modal-close]');
      closeButton?.focus();
    }, 100);

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewItem]);

  const loadDeletedItems = async () => {
    try {
      // Load deleted conversations
      const convResponse = await authenticatedFetch(`${config.apiBaseUrl}/api/conversations/deleted?limit=5`, {});
      if (convResponse.ok) {
        const convData = await convResponse.json();
        setDeletedConversations(convData);
      }

      // Load deleted clipboard items
      const clipResponse = await authenticatedFetch(`${config.apiBaseUrl}/api/clipboard/items/deleted?page=1&page_size=5`, {});
      if (clipResponse.ok) {
        const clipData = await clipResponse.json();
        setDeletedClipboardItems(clipData.items || []);
      }
    } catch (error) {
      console.error('Failed to load deleted items:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsMenuOpen(false);
  };

  const handleDeleteConversation = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation(); // Prevent conversation selection

    if (!confirm('Delete this conversation?')) return;

    setDeletingId(conversationId);
    try {
      await deleteConversation(conversationId);
      toast.success('Conversation deleted');
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeletingId(null);
    }
  };

  const handleConversationClick = async (conversationId: string) => {
    try {
      await loadConversation(conversationId);
      onViewChange('chat'); // Switch to chat view
      onClose(); // Close sidebar on mobile after selecting conversation
      toast.success('Loaded conversation');
    } catch (error) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  const handleNewChat = async () => {
    try {
      await createConversation('New Chat');
      onViewChange('chat'); // Switch to chat view
      onClose();
      toast.success('Started new chat');
    } catch (error) {
      console.error('Failed to create conversation:', error);
      toast.error('Failed to create new chat');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handleCopyToClipboard = async (item: any) => {
    try {
      const markdownContent = item.content_variants?.markdown || item.content_variants?.plain || '';
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const htmlContent = await convertMarkdownToHtml(markdownContent);
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([markdownContent], { type: 'text/plain' })
        });
        await navigator.clipboard.write([clipboardItem]);
        toast.success('Copied to clipboard with formatting!');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(markdownContent);
        toast.success('Copied to clipboard!');
      } else {
        toast.error('Clipboard not available in this browser');
      }
    } catch (error) {
      try {
        const plainContent = item.content_variants?.plain || '';
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(plainContent);
          toast.success('Copied to clipboard!');
        } else {
          toast.error('Failed to copy to clipboard');
        }
      } catch (fallbackError) {
        toast.error('Failed to copy to clipboard');
      }
    }
  };

  const handleDeleteItem = async (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    if (!confirm('Delete this clipboard item?')) return;
    setDeletingId(itemId);
    try {
      await deleteItem(itemId);
      // Refresh clipboard items list to update badge count
      await loadItems();
      // Refresh trash count after deletion
      await loadDeletedItems();
      // Refresh stats to update the badge counts
      await loadStats();
    } catch (error) {
      console.error('Failed to delete clipboard item:', error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed md:relative inset-y-0 left-0 z-50
        w-full sm:w-80 md:w-72 lg:w-80 h-full bg-dark-sidebar flex flex-col border-r border-dark-border
        transform transition-transform duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Header */}
        <div className="p-3 sm:p-5 flex items-center justify-between border-b border-dark-border">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Back arrow for mobile */}
            <button
              onClick={onClose}
              className="md:hidden p-1.5 -ml-1 text-dark-text-muted hover:text-dark-text active:bg-dark-card rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
            </button>

            {/* Logo */}
            <img src={logo} alt="PARACLETE" className="h-10 w-auto" />
            <h1 className="text-xl font-bold bg-gradient-to-r from-accent-blue to-accent-orange bg-clip-text text-transparent">
              PARACLETE
            </h1>
          </div>
        </div>


      {/* Clipboard Manager & Conversations List */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Clipboard Manager Section */}
        <div className="mb-6">
          <div className={`w-full mb-3 flex items-center justify-between p-2 rounded-lg transition-colors ${
            currentView === 'clipboard' ? 'bg-accent-blue/10' : 'hover:bg-dark-card/50'
          }`}>
            <button
              onClick={() => {
                onViewChange('clipboard');
                setIsClipboardExpanded(!isClipboardExpanded);
              }}
              className="flex items-center gap-2 flex-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className={`w-3 h-3 transition-transform ${isClipboardExpanded ? 'rotate-90' : ''}`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className={`text-sm font-semibold uppercase ${currentView === 'clipboard' && isClipboardExpanded ? 'text-accent-orange' : 'text-dark-text-muted'}`}>
                Bridge Items
              </span>
            </button>
            {stats && (
              <button
                onClick={() => { onViewChange('clipboard'); }}
                className="group relative px-3 py-0.5 bg-accent-blue/20 hover:bg-dark-card hover:border hover:border-accent-blue rounded-full text-xs font-semibold text-gray-100 min-w-[60px] text-center cursor-pointer transition-all duration-200 hover:scale-105"
                title="Manage Bridge Items"
              >
                <span className="group-hover:opacity-0 transition-opacity duration-200">
                  {stats.total_clipboard_items}/{stats.clipboard_limit}
                </span>
                <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-dark-text font-semibold">
                  Manage
                </span>
              </button>
            )}
          </div>

          <div
            className="collapsible-section transition-all duration-300 ease-in-out"
            style={{
              maxHeight: isClipboardExpanded ? '2000px' : '0',
              opacity: isClipboardExpanded ? 1 : 0
            }}
          >
            <div className="space-y-2 pt-2">
              {clipboardItems.length === 0 ? (
                <div className="text-xs text-dark-text-muted py-2">No clipboard items yet</div>
              ) : (
                <>
                  {clipboardItems.slice(0, 5).map((item) => {
                    const markdownPreview = item.content_variants?.markdown || item.content_variants?.plain || '';
                    return (
                      <div
                        key={item.id}
                        onClick={() => setPreviewItem(item)}
                        className="p-3 rounded-lg border border-dark-border hover:border-accent-orange bg-dark-card cursor-pointer transition-colors"
                      >
                        <div className="text-sm font-semibold text-dark-text mb-1 truncate">
                          {item.title || 'Untitled'}
                        </div>
                        {markdownPreview && (
                          <div className="text-xs text-dark-text-muted line-clamp-2 mt-1 mb-1">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                p: ({ node, ...props }: any) => <span className="text-dark-text-muted" {...props} />,
                                strong: ({ node, ...props }: any) => <strong className="text-dark-text font-bold" {...props} />,
                                em: ({ node, ...props }: any) => <em {...props} />,
                                code: ({ node, ...props }: any) => <code className="bg-dark-bg px-1 py-0.5 rounded text-xs font-mono text-accent-blue" {...props} />,
                                h1: ({ node, ...props }: any) => <span className="font-bold text-dark-text" {...props} />,
                                h2: ({ node, ...props }: any) => <span className="font-bold text-dark-text" {...props} />,
                                h3: ({ node, ...props }: any) => <span className="font-bold text-dark-text" {...props} />,
                                li: ({ node, ...props }: any) => <span {...props} />,
                                ul: ({ node, ...props }: any) => <span {...props} />,
                                ol: ({ node, ...props }: any) => <span {...props} />,
                              }}
                            >
                              {markdownPreview.substring(0, 150)}
                            </ReactMarkdown>
                          </div>
                        )}
                        <div className="text-xs text-dark-text-muted mt-1">
                          {item.source} • {new Date(item.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    );
                  })}
                  {clipboardTotal > 5 && (
                    <button
                      onClick={() => {
                        onViewChange('clipboard');
                        onClose();
                      }}
                      className="w-full text-xs text-accent-orange hover:text-accent-orange/80 font-semibold text-center py-2 hover:underline"
                    >
                      See All Bridge Items
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Documents Section - Hidden for Basic tier users */}
        {user?.subscription_tier?.toLowerCase() !== 'basic' && (
          <div className="mb-6">
            <div className={`w-full mb-3 flex items-center justify-between p-2 rounded-lg transition-colors ${
              (currentView === 'documents' || currentView === 'file-upload' || currentView === 'folders') ? 'bg-accent-blue/10' : 'hover:bg-dark-card/50'
            }`}>
              <button
                onClick={() => {
                  onViewChange('documents');
                  setIsDocumentsExpanded(!isDocumentsExpanded);
                }}
                className="flex items-center gap-2 flex-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className={`w-3 h-3 transition-transform ${isDocumentsExpanded ? 'rotate-90' : ''}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
                <span className={`text-sm font-semibold uppercase ${(currentView === 'documents' || currentView === 'file-upload' || currentView === 'folders') && isDocumentsExpanded ? 'text-accent-orange' : 'text-dark-text-muted'}`}>
                  {/* Show "Denominational Documents" for superusers, "Documents" for regular users */}
                  {user?.is_superuser ? 'Denominational Documents' : 'Documents'}
                </span>
              </button>
              {/* Denomination badge - Only visible to superusers */}
              {user?.is_superuser && user?.denomination && (
                <span className="text-xs px-2 py-0.5 bg-dark-card rounded-full uppercase text-dark-text-muted">
                  {user.denomination}
                </span>
              )}
            </div>

            <div
              className="collapsible-section transition-all duration-300 ease-in-out"
              style={{
                maxHeight: isDocumentsExpanded ? '2000px' : '0',
                opacity: isDocumentsExpanded ? 1 : 0
              }}
            >
              <div className="space-y-2 pt-2">
                {/* Browse Documents - Shown to all non-Basic users */}
                <button
                  onClick={() => {
                    onViewChange('documents');
                    onClose();
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    currentView === 'documents'
                      ? 'bg-accent-blue/10 border-l-2 border-accent-orange'
                      : 'hover:bg-dark-card/50'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                  <span className={`text-sm font-medium ${currentView === 'documents' ? 'text-accent-orange' : 'text-dark-text'}`}>
                    Browse Documents
                  </span>
                </button>

                {/* Upload Documents - For superusers and non-basic users */}
                <button
                  onClick={() => {
                    onViewChange('file-upload');
                    onClose();
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    currentView === 'file-upload'
                      ? 'bg-accent-blue/10 border-l-2 border-accent-orange'
                      : 'hover:bg-dark-card/50'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  <span className={`text-sm font-medium ${currentView === 'file-upload' ? 'text-accent-orange' : 'text-dark-text'}`}>
                    Upload Documents
                  </span>
                </button>

                {/* Folder Manager - Hidden for Basic tier users (already hidden by parent condition) */}
                <button
                  onClick={() => {
                    onViewChange('folders');
                    onClose();
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    currentView === 'folders'
                      ? 'bg-accent-blue/10 border-l-2 border-accent-orange'
                      : 'hover:bg-dark-card/50'
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <span className={`text-sm font-medium ${currentView === 'folders' ? 'text-accent-orange' : 'text-dark-text'}`}>
                    Folder Manager
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Conversations Section */}
        <div className="mb-6">
          <div className={`w-full mb-3 flex items-center justify-between p-2 rounded-lg transition-colors ${
            currentView === 'chat' ? 'bg-accent-blue/10' : 'hover:bg-dark-card/50'
          }`}>
            <button
              onClick={() => {
                onViewChange('chat');
                setIsConversationsExpanded(!isConversationsExpanded);
              }}
              className="flex items-center gap-2 flex-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className={`w-3 h-3 transition-transform ${isConversationsExpanded ? 'rotate-90' : ''}`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className={`text-sm font-semibold uppercase ${currentView === 'chat' && isConversationsExpanded ? 'text-accent-orange' : 'text-dark-text-muted'}`}>
                Virtual Assistant
              </span>
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={handleNewChat}
                className="px-2.5 py-0.5 bg-gradient-to-r from-accent-orange to-pink-500 hover:from-accent-orange/90 hover:to-pink-500/90 rounded-full text-xs font-semibold text-white transition-all transform hover:scale-105"
                title="Start a new conversation"
              >
                New Chat
              </button>
              {stats && (
                <button
                  onClick={() => { onViewChange('chat-manage'); onClose(); }}
                  className="group relative px-3 py-0.5 bg-accent-blue/20 hover:bg-dark-card hover:border hover:border-accent-blue rounded-full text-xs font-semibold text-gray-100 min-w-[60px] text-center cursor-pointer transition-all duration-200 hover:scale-105"
                  title="Manage conversations"
                >
                  <span className="group-hover:opacity-0 transition-opacity duration-200">
                    {stats.total_conversations}/{stats.conversation_limit}
                  </span>
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-dark-text font-semibold">
                    Manage
                  </span>
                </button>
              )}
            </div>
          </div>
          <div
            className="collapsible-section transition-all duration-300 ease-in-out"
            style={{
              maxHeight: isConversationsExpanded ? '2000px' : '0',
              opacity: isConversationsExpanded ? 1 : 0
            }}
          >
            <div className="space-y-2 pt-2">
              {conversations.length === 0 ? (
                <div className="text-dark-text-muted text-sm px-2">No conversations yet</div>
              ) : (
                <>
                  {conversations.slice(0, 5).map((conversation) => (
                    <ConversationItem
                      key={conversation.id}
                      id={conversation.id}
                      title={conversation.title}
                      updatedAt={conversation.updated_at}
                      isActive={currentConversation?.id === conversation.id}
                      isDeleting={deletingId === conversation.id}
                      onClick={() => handleConversationClick(conversation.id)}
                      onDelete={async () => {
                        setDeletingId(conversation.id);
                        try {
                          await deleteConversation(conversation.id);
                          // Refresh conversations list to update badge count
                          await loadConversations();
                          // Refresh the deleted items list to get accurate count
                          await loadDeletedItems();
                          // Refresh stats to update badge counts
                          await loadStats();
                          toast.success('Conversation moved to trash');
                        } catch (error) {
                          toast.error('Failed to delete conversation');
                        } finally {
                          setDeletingId(null);
                        }
                      }}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Trash Section */}
        <div className="mb-6">
          <button
            onClick={() => {
              onViewChange('trash');
              setIsTrashExpanded(!isTrashExpanded);
            }}
            className={`w-full mb-3 flex items-center justify-between p-2 rounded-lg transition-colors ${
              currentView === 'trash' ? 'bg-accent-blue/10' : 'hover:bg-dark-card/50'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className={`w-3 h-3 transition-transform ${isTrashExpanded ? 'rotate-90' : ''}`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className={`text-sm font-semibold uppercase ${currentView === 'trash' && isTrashExpanded ? 'text-accent-orange' : 'text-dark-text-muted'}`}>
                Trash
              </span>
            </div>
            {(deletedConversations.length + deletedClipboardItems.length) > 0 && (
              <div className="px-2 py-0.5 bg-red-500/20 rounded-full text-xs font-semibold text-red-400">
                {deletedConversations.length + deletedClipboardItems.length}
              </div>
            )}
          </button>

          <div
            className="collapsible-section transition-all duration-300 ease-in-out"
            style={{
              maxHeight: isTrashExpanded ? '2000px' : '0',
              opacity: isTrashExpanded ? 1 : 0
            }}
          >
            <div className="space-y-3 pt-2">
              {/* Deleted Conversations */}
              {deletedConversations.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-dark-text-muted uppercase mb-2 px-2">
                    Deleted Conversations
                  </div>
                  <div className="space-y-2">
                    {deletedConversations.map((item) => (
                      <div
                        key={item.id}
                        onClick={onClose}
                        className="p-3 rounded-lg border border-dark-border hover:border-red-400/50 bg-dark-card cursor-pointer transition-colors"
                      >
                        <div className="text-sm text-dark-text truncate">
                          {item.title || 'Untitled'}
                        </div>
                        <div className="text-xs text-dark-text-muted mt-1">
                          Deleted {new Date(item.deleted_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deleted Clipboard Items */}
              {deletedClipboardItems.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-dark-text-muted uppercase mb-2 px-2">
                    Deleted Clipboard Items
                  </div>
                  <div className="space-y-2">
                    {deletedClipboardItems.map((item) => {
                      const plainContent = item.content_variants?.plain || '';
                      return (
                        <div
                          key={item.id}
                          onClick={onClose}
                          className="p-3 rounded-lg border border-dark-border hover:border-red-400/50 bg-dark-card cursor-pointer transition-colors"
                        >
                          {item.title && (
                            <div className="text-sm font-semibold text-dark-text mb-1 truncate">
                              {item.title}
                            </div>
                          )}
                          <div className="text-sm text-dark-text truncate">
                            {plainContent.substring(0, 50)}{plainContent.length > 50 ? '...' : ''}
                          </div>
                          <div className="text-xs text-dark-text-muted mt-1">
                            Deleted {new Date(item.deleted_at).toLocaleDateString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {deletedConversations.length === 0 && deletedClipboardItems.length === 0 && (
                <div className="text-xs text-dark-text-muted px-2">No deleted items</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - User Menu */}
      <div className="relative border-t border-dark-border">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="w-full p-4 hover:bg-dark-card/50 transition-colors text-left flex items-center gap-3"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-blue to-accent-orange flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-dark-text truncate">
              {user?.full_name || 'User'}
            </div>
            <div className="text-xs text-dark-text-muted truncate">
              {user?.subscription_tier || 'Free'} plan
            </div>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`w-4 h-4 text-dark-text-muted transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {isMenuOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-dark-card border border-dark-border rounded-lg shadow-2xl overflow-hidden">
            {/* User Info at top */}
            <div className="px-4 py-3 border-b border-dark-border bg-dark-bg/50">
              <div className="text-xs text-dark-text-muted mb-2">Signed in as</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-blue to-accent-orange flex items-center justify-center text-white font-semibold flex-shrink-0">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-dark-text font-medium truncate">
                    {user?.full_name || 'User'}
                  </div>
                  <div className="text-xs text-dark-text-muted truncate">
                    {user?.email}
                  </div>
                </div>
              </div>

              {/* Subscription Status Display */}
              <div className="mb-3">
                <SubscriptionStatus compact={true} showDetails={false} />
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  openSettings('profile');
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-dark-text hover:bg-dark-bg/50 transition-colors flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>

              {/* Install Web App - only show if installable and not already in standalone mode */}
              {isInstallable && !isStandalone && (
                <button
                  onClick={async () => {
                    setIsMenuOpen(false);
                    const success = await promptInstall();
                    if (success) {
                      toast.success('App installed successfully!');
                    }
                  }}
                  disabled={isInstalling}
                  className="w-full px-4 py-2.5 text-left text-sm text-accent-blue hover:bg-dark-bg/50 transition-colors flex items-center gap-3 disabled:opacity-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  {isInstalling ? 'Installing...' : 'Install Web App'}
                </button>
              )}

              <div className="my-1 border-t border-dark-border"></div>

              <button
                onClick={handleLogout}
                className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-dark-bg/50 transition-colors flex items-center gap-3"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                </svg>
                Log out
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Clipboard Item Preview Modal */}
      {previewItem && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={() => setPreviewItem(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div
            className="bg-dark-card border border-dark-border rounded-lg max-w-5xl w-full max-h-[90vh] sm:max-h-[80vh] flex flex-col text-sm sm:text-base"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between p-3 sm:p-4 lg:p-6 border-b border-dark-border">
              <div className="flex-1 min-w-0 pr-2">
                <h2 id="modal-title" className="text-lg sm:text-xl font-semibold text-dark-text mb-2 truncate">
                  {previewItem.title || 'Clipboard Item'}
                </h2>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-dark-muted">
                  <span className="px-2 py-1 bg-accent-blue/20 border border-accent-blue/30 rounded text-xs font-bold text-accent-blue uppercase w-fit">
                    {previewItem.source}
                  </span>
                  <span>{formatDate(previewItem.created_at)}</span>
                  <span>{previewItem.character_count || 0} chars • {previewItem.word_count || 0} words</span>
                </div>
              </div>
              <button
                onClick={() => setPreviewItem(null)}
                data-modal-close
                aria-label="Close preview"
                className="text-dark-muted hover:text-dark-text active:text-dark-text transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -m-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
              {/* Check for image content */}
              {previewItem.content_variants?.image && (
                <div className="mb-4">
                  <img
                    src={previewItem.content_variants.image}
                    alt={previewItem.title || 'Clipboard image'}
                    className="max-w-full h-auto rounded-lg border border-dark-border"
                  />
                </div>
              )}

              {/* Display HTML content if available */}
              {previewItem.content_variants?.html && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">HTML Content</div>
                  <div
                    className="bg-dark-bg border border-dark-border rounded-lg p-4 prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewItem.content_variants.html) }}
                  />
                </div>
              )}

              {/* Display markdown content with styling */}
              {(previewItem.content_variants?.markdown || previewItem.content_variants?.plain) && !previewItem.content_variants?.html && (
                <div className="mb-4">
                  <div className="bg-dark-bg border border-dark-border rounded-lg p-4 max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({ node, children, ...props }: any) => (
                          <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border border-dark-border" {...props}>{children}</table>
                          </div>
                        ),
                        thead: ({ node, ...props }: any) => <thead className="bg-dark-bg" {...props} />,
                        th: ({ node, ...props }: any) => <th className="border border-dark-border px-4 py-2 text-left font-semibold text-dark-text" {...props} />,
                        td: ({ node, ...props }: any) => <td className="border border-dark-border px-4 py-2 text-dark-text" {...props} />,
                        tbody: ({ node, ...props }: any) => <tbody {...props} />,
                        tr: ({ node, ...props }: any) => <tr className="hover:bg-dark-card/50" {...props} />,
                        h1: ({ node, ...props }: any) => <h1 className="text-2xl font-bold mb-4 mt-6 text-dark-text" {...props} />,
                        h2: ({ node, ...props }: any) => <h2 className="text-xl font-bold mb-3 mt-5 text-dark-text" {...props} />,
                        h3: ({ node, ...props }: any) => <h3 className="text-lg font-bold mb-2 mt-4 text-dark-text" {...props} />,
                        p: ({ node, ...props }: any) => <p className="mb-3 text-dark-text" {...props} />,
                        strong: ({ node, ...props }: any) => <strong className="text-dark-text font-bold" {...props} />,
                        li: ({ node, ...props }: any) => <li className="text-dark-text ml-4" {...props} />,
                        ul: ({ node, ...props }: any) => <ul className="list-disc list-inside mb-3" {...props} />,
                        ol: ({ node, ...props }: any) => <ol className="list-decimal list-inside mb-3" {...props} />,
                        blockquote: ({ node, ...props }: any) => <blockquote className="border-l-4 border-accent-blue pl-4 italic my-4 text-dark-text-muted" {...props} />,
                        code: ({ node, inline, ...props }: any) => inline ? (
                          <code className="bg-dark-bg px-1.5 py-0.5 rounded text-sm font-mono text-accent-blue" {...props} />
                        ) : (
                          <code className="block bg-dark-bg rounded-lg p-4 overflow-x-auto border border-dark-border text-xs sm:text-sm" {...props} />
                        ),
                      }}
                    >
                      {previewItem.content_variants.markdown || previewItem.content_variants.plain}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {/* If no content variants, show title or fallback */}
              {!previewItem.content_variants?.plain && !previewItem.content_variants?.html && !previewItem.content_variants?.image && (
                <div className="bg-dark-bg border border-dark-border rounded-lg p-4">
                  <p className="text-dark-muted text-sm italic">
                    {previewItem.title || 'No content available'}
                  </p>
                </div>
              )}

              {/* Display all available content variants */}
              {previewItem.content_variants && Object.keys(previewItem.content_variants).length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">
                    Available Formats: {Object.keys(previewItem.content_variants).join(', ')}
                  </div>
                </div>
              )}

              {/* Tags */}
              {previewItem.tags && previewItem.tags.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-dark-muted mb-2">Tags</div>
                  <div className="flex flex-wrap gap-2">
                    {previewItem.tags.map((tag: string, index: number) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-accent-blue/10 text-accent-blue text-xs rounded"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-3 sm:p-4 lg:p-6 border-t border-dark-border">
              <button
                onClick={() => {
                  handleDeleteItem(new MouseEvent('click') as any, previewItem.id);
                  setPreviewItem(null);
                }}
                className="px-4 py-3 sm:py-2 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 active:bg-red-500/30 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
                <span className="text-sm sm:text-base">Delete</span>
              </button>
              <button
                onClick={() => {
                  handleCopyToClipboard(previewItem);
                }}
                className="px-4 py-3 sm:py-2 bg-accent-blue text-white rounded hover:bg-accent-blue/80 active:bg-accent-blue/70 transition-colors flex items-center justify-center gap-2 min-h-[44px] sm:min-h-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                </svg>
                <span className="text-sm sm:text-base">Send to Clipboard</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
